<script>
import { mapState } from 'vuex'
import Rainbow from '@/components/Rainbow.vue'
import SignupProgress from '@/components/SignupProgress.vue'
import PlanCards from '@/components/PlanCards.vue'
import AddPet from '@/components/AddPet.vue'
import PetDisplay from '@/components/PetDisplay.vue'
import Input from '@/components/Input.vue'
import AutocompleteSelect from '@/components/AutocompleteSelect.vue'
import Validation from '@/plugins/validation'
import Vue from 'vue'
import track from '@/plugins/track'

export default {
  name: 'Signup',
  props: ['id'],
  components: {
    Rainbow,
    SignupProgress,
    PlanCards,
    AddPet,
    PetDisplay,
    Input,
    AutocompleteSelect,
  },
  data() {
    return {
      paymentTab: 0,
      petsValid: false,
      personalDetailsValid: false,
      billingValid: false,
      ...Validation,
    }
  },
  async mounted() {
    await this.$store.dispatch('GET_PLANS')
    this.$store.dispatch('signup/UPDATE_LAST_PET_PLAN', this.plan)
    this.$scrollTo(`#app`, 500, { easing: 'ease-in-out', })
  },
  computed: {
    ...mapState({
      practice: state => state.selectedPractice,
      plans: state => state.plans,
      pets: state => state.signup.pets,
      steps: state => state.signup.steps,
      signup: state => state.signup,
      status: state => state.loader.status,
    }),
    step: {
      get() {
        return this.$store.state.signup.currentStep
      },
      set(v) {
        this.$scrollTo(`#app`, 800, { easing: 'ease-in-out', })
        this.$store.dispatch('signup/SET_STEP', v)
      },
    },
    plan() {
      return this.plans.find(plan => plan.id == this.id)
    },
    // Form fields below, need a better way to handle this instead of matching each field w/ a computed property
    optIn: {
      get() {
        return this.$store.state.signup.optIn
      },
      set(v) {
        this.$store.dispatch('signup/SET_FIELD', { key: 'optIn', value: v })
      },
    },
    years() {
      return [...Array(10)].map((inc, i) => {
        return new Date().getFullYear() + i
      })
    },
    months() {
      return [ "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ].map((month, i) => {
        let digit = i + 1
        if(i <= 9) {
          digit = '0' + (i + 1)
        }
        return { text: `${ i + 1 } - ${ month }`, value: digit }
      })
    }
  },
  methods: {
    addPet() {
      if(this.$refs.petsForm.validate()) {
        this.$store.dispatch('signup/ADD_PET', { name: '', breed: '', plan: this.plan, dob: '', couponCode: '', apaId: '', species: '' })
        track({
          event: 'click',
          category: 'signup',
          action: 'addpet',
          label: 'AddPetClicked',
        })
      }
    },
    validate(ref) {
      if(this.$refs[ref].validate()) {
        if(ref == 'petsForm') {
          this.step = 1
          track({
            event: 'click',
            category: 'signup',
            action: 'ownerdetails',
            label: 'OwnerDetailsClicked',
          })
        }
        else if(ref == 'personalDetailsForm') {
          this.step = 2
          track({
            event: 'click',
            category: 'signup',
            action: 'payment',
            label: 'PaymentClicked',
          })
        }
      }
    },
    async finish() {
      if(this.$refs.billingForm.validate()) {
        track({
          event: 'click',
          category: 'signup',
          action: 'processpayment',
          label: 'ProcessPaymentClicked',
        })
        await this.$store.dispatch('signup/SIGNUP')
        if(this.status === 'COMPLETE') {
          this.$router.push({ name: 'ThankYou' })
        }
      }
    },
    gotoStep0() {
      this.step = 0
      track({
        event: 'click',
        category: 'signup',
        action: 'backtopetdetails',
        label: 'BackToPetDetailsClicked',
      })
    },
    gotoStep1() {
      this.step = 1
      track({
        event: 'click',
        category: 'signup',
        action: 'backtoownerdetails',
        label: 'BackToOwnerDetailsClicked',
      })
    },
  }
}
</script>

<template>
  <v-container fluid class="grey lighten-4 mx-auto pa-0">
    <v-row v-if="step === 0">
      <v-col class="mx-auto" align="center" md="6" cols="12">
        <h1 class="mt-8">Enroll your pet</h1>
        <Rainbow class="ma-0" thickness="8px" style="text-align: left; width: 30%;" />
        <p>
          Tell us a bit about your pet and we'll get the enrollment process started.<br />
          Don't worry - signing up only takes a few minutes.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-container class="">
          <v-row class="section signup">
            <v-col id="signup">
              <v-carousel
                v-model="step"
                width="100%"
                :show-arrows="false"
                height="100%"
                hide-delimiters
                hide-delimiter-background
              >
                <v-carousel-item>
                  <div class="inset elevation-4 mx-auto addpet">
                    <div id="pets" :class="{ disabled: step != 0 }" class="white my-8 pa-8">
                      <div class="stepprogress mb-4">
                        <h2>Pet Details</h2>
                      </div>
                      <SignupProgress :step="step" />
                      <v-row>
                        <v-col align="left">
                          <v-divider light></v-divider>
                          <h3 class="mt-4 pl-4">Pet Details</h3>
                        </v-col>
                      </v-row>
                      
                      <v-form ref="petsForm" v-model="petsValid">
                        <v-container class="pt-0">
                          <AddPet @addPet="addPet" v-for="(pet, i) in pets" :key="i" :index="i" :pet="pet" />
                          
                          <v-row align="center" justify="center" class="mt-4">
                            <v-col md="6" cols="12" class="mt-0 mb-0" align="left" align-self="start" style="font-size: 12px" >
                              <p class="mb-0">*Requires minimum 12 month commitment</p>
                            </v-col>
                            <v-col md="6" cols="12" align="right" align-self="center">
                              <v-btn class="greenbtn" large @click="addPet">Add New Pet<v-icon class="ml-4">mdi-plus-box</v-icon></v-btn>
                            </v-col>
                          </v-row>
                          
                          <v-row align="center" justify="center">
                            <v-col cols="12" md="12" align="right">
                              <v-btn class="ml-auto" large color="primary" @click="validate('petsForm')">Owner Details <v-icon class="ml-4">mdi-arrow-right-box</v-icon></v-btn>
                            </v-col>
                          </v-row>
                        </v-container>

                      </v-form>
                    </div>
                  </div>
                </v-carousel-item>
                <v-carousel-item>
                  <div id="personal" :class="{ disabled: step != 1 }" class="inset elevation-4 mx-auto white my-8 pa-2">
                    <div class="stepprogress mt-6">
                      <h2>Owner Details</h2>
                    </div>
                    <SignupProgress :step="step" />
                    <v-form ref="personalDetailsForm" v-model="personalDetailsValid">
                      <v-container align="center" justify="center" color="white" height="100%" class="pa-4">
                        <v-row>
                          <v-col align="left">
                            <v-divider light></v-divider>
                            <h3 class="mt-4">Owner Details</h3>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col md="6" cols="12">
                            <Input form="signup" field="firstName" :rules="[...nameRules, ...required]" placeholder="" label="First Name" />
                          </v-col>
                          <v-col md="6" cols="12">
                            <Input form="signup" field="lastName" :rules="[...nameRules, ...required]" placeholder="" label="Last Name" />
                          </v-col>
                          <v-col cols="12">
                            <Input form="signup" field="email" :rules="emailRules" placeholder="" label="Email" />
                          </v-col>
                          <v-col md="6" cols="10">
                            <Input form="signup" field="mobile" :rules="phoneRules" placeholder="XXX-XXX-XXXX" label="Mobile" v-mask="'###-###-####'"/>
                          </v-col>
                          <v-col align="left">
                            <v-tooltip right color="primary">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  color="primary"
                                  class="d-inline-block mt-3"
                                  v-bind="attrs"
                                  v-on="on"
                                >mdi-help-circle-outline</v-icon>
                              </template>
                              <span>Please provide your phone number in case we need to<br />contact you about your membership. We promise not to <br />share it with anyone or use it for marketing purposes.</span>
                          </v-tooltip>
                          </v-col>
                          <v-col cols="12" class="mb-0 pb-0">
                            <v-checkbox
                              v-model="optIn"
                              label="Yes, I would like to receive emails with news, pet care tips and promotional offers."
                              light
                            ></v-checkbox>
                          </v-col>
                          <v-col class="mt-0 pt-0" align="left" cols="12">
                            <router-link target="_blank" :to="{ name: 'PrivacyPolicy' }">Privacy Policy</router-link>
                            <v-divider light class="my-4 mt-8"></v-divider>
                          </v-col>
                        </v-row>
                        <v-row align="center" justify="center">
                          <v-col cols="12" md="6" align="left">
                            <v-btn large light text class="my-0" @click="gotoStep0"><v-icon class="mr-3">mdi-arrow-left</v-icon>Pet Details</v-btn>
                          </v-col>
                          <v-col cols="12" md="6" align="right">
                            <v-btn large class="my-0" color="primary" @click="validate('personalDetailsForm')">Payment <v-icon class="ml-3">mdi-arrow-right-box</v-icon></v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>
                  </div>
                </v-carousel-item>

                <!-- PAYMENT -->

                <v-carousel-item>
                  <div id="payment" :class="{ disabled: step != 2 }" class="inset elevation-4 mx-auto white my-8 py-8 pb-2">
                    <div class="stepprogress mb-2">
                      <h2>Payment</h2>
                    </div>
                    <SignupProgress :step="step" />
                    <v-form ref="billingForm" v-model="billingValid">
                      <v-container align="center" justify="center" color="white" height="100%" class="py-4">
                        <v-row>
                          <v-col align="left">
                            <v-divider light></v-divider>
                            <PetDisplay />
                            <v-row>
                              <v-col class="ownerdetails mb-4 px-12">
                                <p class="mt-2"><v-icon light small>mdi-arrow-left</v-icon><span @click="step = 0" class="backbtn">EDIT PET & PLAN DETAILS</span></p>
                                <v-divider light class="my-4"></v-divider>
                                <h3 class="mb-2">Owner Details</h3>
                                <p>{{ signup.firstName }} {{ signup.lastName }}</p>
                                <p>{{ signup.email }}</p>
                                <p>{{ signup.mobile }}</p>
                                <p class="mt-2"><v-icon light small>mdi-arrow-left</v-icon><span @click="step = 1" class="backbtn">EDIT OWNER DETAILS</span></p>
                              </v-col>
                            </v-row>
                            <v-row width="100%" class="paymentinformation">
                              <v-col class="pa-8 px-12">
                                  <h3 style="color: white;">Payment Information</h3>
                              </v-col>
                            </v-row>
                            <div class="pa-8">
                              <v-tabs
                                v-model="paymentTab"
                                class="mt-4"
                                background-color="white"
                                color="primary"
                                light
                              >
                                <v-tabs-slider></v-tabs-slider>
                                <v-tab @click="$store.dispatch('signup/SET_FIELD', { key: 'paymentType', value: 'CC' })" href="#tab-cc">Credit Card</v-tab>
                                <v-tab-item
                                  value="tab-cc"
                                >
                                  <v-container align="center" justify="center" color="white" height="100%" class="pa-4">
                                    <v-row>
                                      <v-col cols="12">
                                        <img class="mb-4" src="@/assets/img/creditcards.jpg" />
                                        <Input form="signup" field="nameOnCard" :rules="[...nameRules, ...required]" placeholder="" label="Name On Card" />
                                      </v-col>
                                      <v-col cols="12">
                                        <Input form="signup" type="number" maxlength="19" field="ccNumber" :rules="ccRules" placeholder="" label="Credit Card Number" />
                                      </v-col>
                                      <v-col md="5" cols="12">
                                        <AutocompleteSelect form="signup" field="expMonth" :rules="required" placeholder="" label="Month" :items="months" item-text="text" item-value="value" return-object />
                                      </v-col>
                                      <v-col md="5" cols="12">
                                        <AutocompleteSelect form="signup" field="expYear" :rules="required" placeholder="" label="Year" :items="years" />
                                      </v-col>
                                      <v-col md="2" cols="12">
                                        <Input form="signup" type="number" maxlength="4" field="cvv" :rules="[...cvvRules, ...required]" placeholder="" label="CVV" />
                                      </v-col>
                                    </v-row>
                                    <v-row>
                                      <v-col align="left">
                                        <h3>Billing Address</h3>
                                      </v-col>
                                    </v-row>
                                    <v-row>
                                      <v-col cols="12">
                                        <Input form="signup" field="address" :rules="[...nameRules, ...required]" placeholder="" label="Address" />
                                      </v-col>
                                      <v-col cols="12">
                                        <Input form="signup" field="address2" :rules="nameRules" placeholder="" label="Address 2" />
                                      </v-col>
                                      <v-col md="4" cols="12">
                                        <Input form="signup" field="city" :rules="[...nameRules, ...required]" placeholder="" label="City" />
                                      </v-col>
                                      <v-col md="4" cols="12">
                                        <AutocompleteSelect form="signup" field="state" :rules="required" placeholder="" label="State" :items="states" />
                                      </v-col>
                                      <v-col md="4" cols="12">
                                        <Input form="signup" field="zip" :rules="[...zipRules, ...required]" placeholder="" label="Zip" />
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-tab-item>
                              </v-tabs>
                            </div>
                            <div class="px-12">
                              <h3>Monthly Terms Agreement</h3>
                              <v-checkbox
                                v-model="optIn"
                                :rules="[...required]"
                                label="I understand that by joining Healthy Pet + it requires a minimum 12-month commitment, fees may apply if I cancel my membership early and that I have read and understand the full terms & conditions provided in the link below."
                                light
                              ></v-checkbox>
                              <router-link class="pl-8" style="color: black;" target="_blank" :to="{ name: 'TermsAndConditions'}">View Full Terms & Conditions</router-link>
                            </div>
                            <v-divider light class="my-4 mt-8"></v-divider>
                          </v-col>
                        </v-row>
                        <v-row class="px-6" align="center" justify="center">
                          <v-col cols="12" md="6" align="left">
                            <v-btn large light text class="my-0" @click="gotoStep1"><v-icon class="mr-3">mdi-arrow-left</v-icon>Owner Details</v-btn>
                          </v-col>
                          <v-col cols="12" md="6" align="right">
                            <v-btn large 
                              class="my-0" 
                              color="primary" 
                              @click="finish" 
                              :disabled="status == 'LOADING' || status == 'ERROR'">        
                                <v-icon class="mr-4">mdi-lock</v-icon> Process Payment
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>
                  </div>
                </v-carousel-item>
              </v-carousel>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row v-if="step === 0" class="ma-0 pa-0">
      <v-col class="ma-0 pa-0">
        <PlanCards :alt="true" class="ma-0 pa-0" />
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
  h3 {
    color: $hpp-blue;
  }
  .disabled {
    display: none;
  }
  .stepprogress {
  }
  .disabled .stepprogress {
    display: none;
  }
  .step-indicator {
    display: inline-block;
    border-top: solid 3px #E2E2E2;
    color: #aaa;
    font-size: 12px;
    padding: 8px 15px 0;
  }
  .activestep {
    border-top-color: $hpp-green;
    color: #333;
  }
  .step {
    font-size: 24px;
  }
  .stepprogress h2 {
    font-size: 32px;
    color: $hpp-darkblue;
    line-height: 36px;

  }
  .progress {
    width: 100%;
    background-color: $hpp-blue;
    height: 20px;
  }
  .progressbg {
    background-color: #CCC;
  }

  .ownerdetails p {
    font-size: 13px;
    margin: 0;
    padding: 0;
  }
  .paymentinformation {
    background-color: $hpp-blue;
    color: white;
  }

  .backbtn {
    text-decoration: underline;
    cursor: pointer;
  }

  @media only screen and (max-width: 960px) {
    h1 {
      font-size: 28px;
      line-height: 28px;
    }
    h2 {
      font-size: 24px;
      line-height: 24px;
    }
    h3 {
      font-size: 16px;
      line-height: 16px;
    }
  }
</style>
